import { Controller } from '@hotwired/stimulus'
import { prefersReducedMotion } from './mixins/reduced_motion'

export default class extends Controller {
  static targets = ['query', 'caret', 'placeholder', 'group', 'results', 'action', 'home', 'search']

  connect () {
    this.cancelled = false
    this.visible = true
    this.scenes = this.buildScenes()

    if (prefersReducedMotion()) {
      this.showHome()
      return
    }

    this.observer = new IntersectionObserver(
      entries => {
        this.visible = entries[0].isIntersecting
        if (this.visible) this.resolveVisible?.()
      },
      { threshold: 0.15 }
    )
    this.observer.observe(this.element)

    this.showHome()
    this.run()
  }

  disconnect () {
    this.cancelled = true
    this.observer?.disconnect()
  }

  async run () {
    let i = 0
    while (!this.cancelled) {
      await this.whileVisible()
      if (this.cancelled) return
      await this.sleep(2200)
      if (this.cancelled) return
      await this.playScene(this.scenes[i % this.scenes.length])
      if (this.cancelled) return
      await this.clearQuery()
      this.showHome()
      i++
    }
  }

  showHome () {
    this.homeTarget.classList.remove('invisible')
    this.searchTarget.classList.add('invisible')
  }

  showSearch () {
    this.homeTarget.classList.add('invisible')
    this.searchTarget.classList.remove('invisible')
  }

  async playScene (scene) {
    this.showSearch()
    await this.type(scene.query)
    if (this.cancelled) return
    await this.sleep(260)
    this.renderResults(scene)
    await this.sleep(2300)
  }

  async type (text) {
    this.placeholderTarget.classList.add('hidden')
    for (let n = 1; n <= text.length; n++) {
      if (this.cancelled) return
      this.queryTarget.textContent = text.slice(0, n)
      await this.sleep(this.rand(45, 95))
    }
  }

  async clearQuery () {
    await this.sleep(450)
    const text = this.queryTarget.textContent
    for (let n = text.length; n >= 0; n--) {
      if (this.cancelled) return
      this.queryTarget.textContent = text.slice(0, n)
      await this.sleep(20)
    }
    this.groupTarget.textContent = ''
    this.resultsTarget.replaceChildren()
    this.placeholderTarget.classList.remove('hidden')
    await this.sleep(280)
  }

  renderResults (scene) {
    this.groupTarget.textContent = scene.group
    if (this.hasActionTarget) {
      this.actionTarget.textContent = scene.rows[0].kind === 'Project' ? 'Open' : 'Run'
    }
    this.resultsTarget.replaceChildren()
    scene.rows.forEach((row, idx) => {
      const el = this.buildRow(row, idx === 0)
      el.style.opacity = '0'
      el.style.transform = 'translateY(5px)'
      this.resultsTarget.appendChild(el)
      setTimeout(() => {
        el.style.transition = 'opacity .28s ease, transform .28s ease'
        el.style.opacity = '1'
        el.style.transform = 'translateY(0)'
      }, 50 + idx * 75)
    })
  }

  buildRow (row, selected) {
    const wrap = document.createElement('div')
    wrap.className =
      'flex items-center gap-3 mx-2 px-3 py-2.5 rounded-xl' +
      (selected ? ' bg-white/55 ring-1 ring-white/60' : '')

    const body = document.createElement('div')
    body.className = 'min-w-0 flex-1 text-left'
    const title = document.createElement('span')
    title.className = 'text-sm font-semibold text-gray-900'
    title.textContent = row.title
    const subtitle = document.createElement('span')
    subtitle.className = 'text-sm text-gray-600 ml-2'
    subtitle.textContent = row.subtitle
    body.append(title, subtitle)

    const kind = document.createElement('span')
    kind.className = 'text-xs text-gray-600 shrink-0'
    kind.textContent = row.kind

    wrap.append(this.iconFor(row.icon), body, kind)
    return wrap
  }

  iconFor (key) {
    const tpl = this.element.querySelector(`template[data-icon="${key}"]`)
    return tpl.content.firstElementChild.cloneNode(true)
  }

  async whileVisible () {
    if (this.visible || this.cancelled) return
    await new Promise(resolve => { this.resolveVisible = resolve })
    this.resolveVisible = null
  }

  sleep (ms) { return new Promise(resolve => setTimeout(resolve, ms)) }
  rand (min, max) { return min + Math.floor(Math.random() * (max - min)) }

  buildScenes () {
    return [
      {
        query: 'Dunder',
        group: 'Basecamp projects',
        rows: [
          { icon: 'basecamp', title: 'Dunder Mifflin — Scranton', subtitle: 'Branch', kind: 'Project' },
          { icon: 'basecamp', title: 'Dunder Mifflin — Utica', subtitle: 'Branch', kind: 'Project' },
          { icon: 'basecamp', title: 'Dunder Mifflin Infinity', subtitle: 'Website', kind: 'Project' }
        ]
      },
      {
        query: 'summarize this thread',
        group: 'Quick actions',
        rows: [
          { icon: 'ai', title: 'AI Buddy', subtitle: 'Ask a question or continue a recent chat', kind: 'Command' }
        ]
      },
      {
        query: 'improve writing',
        group: 'Quick actions',
        rows: [
          { icon: 'ai', title: 'Improve Writing', subtitle: "Tighten the text you've selected, in place", kind: 'Command' },
          { icon: 'ai', title: 'Make Shorter', subtitle: 'Condense without losing the meaning', kind: 'Command' },
          { icon: 'ai', title: 'Translate to Spanish', subtitle: 'Context-aware, not a literal swap', kind: 'Command' }
        ]
      },
      {
        query: '15% of 12,400',
        group: 'Calculator',
        rows: [
          { icon: 'calculator', title: '1,860', subtitle: '↵ to copy', kind: 'Answer' }
        ]
      },
      {
        query: 'my schedule',
        group: 'Quick actions',
        rows: [
          { icon: 'schedule', title: 'Branch meeting w/ Michael', subtitle: '10:30 · ⌘↵ joins the call', kind: 'Command' }
        ]
      },
      {
        query: 'scratch',
        group: 'Quick actions',
        rows: [
          { icon: 'scratchpad', title: 'Scratchpad', subtitle: 'Find, create and open your notes', kind: 'Command' }
        ]
      },
      {
        query: 'trans',
        group: 'Results',
        rows: [
          { icon: 'translate', title: 'Translate to Spanish', subtitle: 'Translate the selected text to Spanish', kind: 'Command' },
          { icon: 'translate', title: 'Translate to English', subtitle: 'Translate the selected text to English', kind: 'Command' },
          { icon: 'darkmode', title: 'Dark Mode', subtitle: 'Switch macOS to Dark Mode', kind: 'Command' }
        ]
      },
      {
        query: 'fig',
        group: 'Results',
        rows: [
          { icon: 'figma', title: 'Figma', subtitle: '', kind: 'App' },
          { icon: 'bluetooth', title: 'Bluetooth File Exchange', subtitle: '', kind: 'App' },
          { icon: 'gif', title: 'GIF Search', subtitle: 'Find and paste a GIF (powered by Giphy)', kind: 'Command' }
        ]
      }
    ]
  }
}
